import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment-timezone";
import { ORDER_TYPES } from "../utils/Constants";

const useScheduledSlots = () => {
  const { hotel } = useSelector((state) => state.hotel);

  // Get buffer time for order type
  const getBufferTime = useCallback((orderType) => {
    if (!hotel) return 30;
    switch (orderType) {
      case ORDER_TYPES.DELIVERY:
        return hotel.delivery_buffer_time || 30;
      case ORDER_TYPES.PICKUP:
        return hotel.pickup_buffer_time || 15;
      default:
        return 30;
    }
  }, [hotel]);

  // Check if hotel is available for ordering
  const isHotelAvailable = useMemo(() => {
      //   hotel: !!hotel,
      //   online_order_active: hotel?.online_order_active,
      //   hotelStatus: hotel?.hotelStatus?.[0]?.active
      // });
    
    if (!hotel) {
      return false;
    }
    
    // Check online order status
    if (hotel.online_order_active !== 1) {
      return false;
    }
    
    // Check hotel status
    if (hotel.hotelStatus?.[0]?.active !== 1) {
      return false;
    }
    
    return true;
  }, [hotel]);

  // Check if a date is a holiday
  const isHoliday = useCallback((date) => {
    // Check both shop_holidays and shop_hoilidays (typo in data)
    const holidays = Array.isArray(hotel?.shop_holidays)
      ? hotel.shop_holidays
      : Array.isArray(hotel?.shop_hoilidays)
      ? hotel.shop_hoilidays
      : [];

    if (holidays.length === 0) return false;

    const dateStr = moment(date).format("YYYY-MM-DD");
    return holidays.some((holiday) => holiday?.date === dateStr);
  }, [hotel?.shop_holidays, hotel?.shop_hoilidays]);

  // Generate time slots for a specific date and order type
  const getTimeSlots = useCallback((selectedDate, orderType) => {
    if (!hotel || !selectedDate || !orderType || !isHotelAvailable) {
      return [];
    }

    const date = moment(selectedDate);
    const dayInfo = hotel.workingInfo?.find(w => w.day === date.day());
    
    if (!dayInfo || dayInfo.is_closed) {
      return [];
    }

    // Find order type specific hours - be strict about order type
    const orderTypeInfo = dayInfo.hotelDayInfo?.find(h => h.type === orderType);
    
    // Only proceed if the specific order type has running hours
    if (!orderTypeInfo || !orderTypeInfo.running_hours?.length) {
      return [];
    }

    const bufferTime = getBufferTime(orderType);
    const slots = [];
    const isToday = date.isSame(moment(), 'day');
    
    // Current time in local timezone
    const now = moment();

    // Process each running hour period
    for (const { start, stop } of orderTypeInfo.running_hours) {
      // The running hours are in UTC format, convert them to local time
      // Create UTC moment objects from the time strings
      const startUtc = moment.utc(start, 'HH:mm:ss');
      const stopUtc = moment.utc(stop, 'HH:mm:ss');
      
      // Convert to local time
      const startLocal = startUtc.local();
      const stopLocal = stopUtc.local();
      
      // Extract the local time values
      const startHour = startLocal.hour();
      const startMin = startLocal.minute();
      const stopHour = stopLocal.hour();
      const stopMin = stopLocal.minute();
      
      
      let currentStart, currentStop;
      
      // Check if this is overnight service (stop time is before start time)
      const isOvernightService = stopHour < startHour || (stopHour === startHour && stopMin < startMin);
      
      if (isOvernightService) {
        
        if (isToday) {
          const nowHour = now.hour();
          const nowMin = now.minute();
          const currentTimeInMinutes = nowHour * 60 + nowMin;
          const serviceStartInMinutes = startHour * 60 + startMin;
          const serviceEndInMinutes = stopHour * 60 + stopMin; // This is for next day
          
          // Check if we're in the evening service period (after start time)
          if (currentTimeInMinutes >= serviceStartInMinutes) {
            currentStart = date.clone().hour(startHour).minute(startMin).second(0);
            currentStop = date.clone().endOf('day');
          }
          // Check if we're in the early morning service period (before end time)
          else if (currentTimeInMinutes <= serviceEndInMinutes) {
            currentStart = date.clone().startOf('day');
            currentStop = date.clone().hour(stopHour).minute(stopMin).second(0);
          }
          // We're between end and start time - no service
          else {
            continue; // Skip this running hour, but check if there are other running hours
          }
        } else {
          currentStart = date.clone().hour(startHour).minute(startMin).second(0);
          currentStop = date.clone().add(1, 'day').hour(stopHour).minute(stopMin).second(0);
        }
      } else {
        // Regular service hours (non-overnight)
        // Use the local converted times for creating the time windows
        currentStart = date.clone().hour(startHour).minute(startMin).second(0);
        currentStop = date.clone().hour(stopHour).minute(stopMin).second(0);
      }

      // Skip if no valid time window was set
      if (!currentStart || !currentStop) {
        continue;
      }

      // Apply buffer time for today
      if (isToday) {
        // Create a fresh local moment for current time to avoid any timezone issues
        const currentLocalTime = moment();
        
        // Calculate minimum start time: current time + buffer time + 15 minutes
        const minTime = currentLocalTime.clone()
          .add(bufferTime, 'minutes')
          .add(15, 'minutes');
        
        if (currentStart.isBefore(minTime)) {
          currentStart = minTime.clone();
          // Round up to next 15-minute interval
          const minutes = currentStart.minutes();
          const roundedMinutes = Math.ceil(minutes / 15) * 15;
          currentStart.minutes(roundedMinutes).seconds(0);
        }
        
        // Check if we still have a valid time window
        if (currentStart.isAfter(currentStop) || currentStart.isSame(currentStop)) {
          continue;
        }
      }

    

      // Generate 15-minute interval slots
      let slotTime = currentStart.clone();
      let slotCount = 0;
      while (slotTime.isBefore(currentStop)) {
        // Ensure the slot time uses the correct date (selectedDate) not just the time
        const slotDateTime = date.clone()
          .hour(slotTime.hour())
          .minute(slotTime.minute())
          .second(0)
          .millisecond(0);
          
        slots.push({
          time: slotTime.format("HH:mm"),
          label: slotTime.format("h:mm A"),
          value: slotDateTime.toISOString(),
          typeOfOrder: "SCHEDULED"
        });
        slotTime.add(15, 'minutes');
        slotCount++;
        if (slotCount > 100) { // Safety break to prevent infinite loops
          break;
        }
      }
      

    }

    // If no slots were generated from any running hours, return empty array
    if (slots.length === 0) {
      return [];
    }

    // Sort slots by time value (chronological order) and remove duplicates
    const uniqueSlots = slots
      .sort((a, b) => {
        // Convert time strings to minutes for proper chronological sorting
        const timeToMinutes = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          return hours * 60 + minutes;
        };
        return timeToMinutes(a.time) - timeToMinutes(b.time);
      })
      .filter((slot, index, arr) => 
        index === 0 || slot.time !== arr[index - 1].time
      );

    return uniqueSlots;
  }, [hotel, isHotelAvailable, getBufferTime]);

  // Generate available dates based on preorder and order_later rules
  const getAvailableDates = useMemo(() => {
    if (!hotel || !isHotelAvailable) {
      return [];
    }

    const dates = [];
    const today = moment();
    const timezone = "Asia/Kolkata"; // or hotel?.time_zone if available
    
    // Determine date range based on preorder and order_later settings
    let startOffset = 0;
    let maxDays = 3; // Default: today, tomorrow, day after tomorrow

    // Handle order_later rules first - this determines the minimum start offset
    if (hotel.order_later === 1 && hotel.order_later_days >= 1) {
      // order_later_days = 1 means can't order today (start from tomorrow)
      // order_later_days = 2 means can't order today or tomorrow (start from day after)
      startOffset = hotel.order_later_days;
    }

    // Handle preorder rules - this determines the maximum days allowed
    if (hotel.is_preorder === 1) {
      if (hotel.pre_min_day === 1) {
        // Can order today only
        maxDays = 1;
      } else if (hotel.pre_min_day === 2) {
        // Can order today and tomorrow
        maxDays = 2;
      } else if (hotel.pre_min_day > 2) {
        // Can order for more days
        maxDays = hotel.pre_min_day;
      }
    }

    // Check for conflicting rules - if startOffset >= maxDays, no dates are available
    if (startOffset >= maxDays) {
      // Conflicting rules: e.g., pre_min_day=1 (today only) but order_later_days=1 (can't order today)
      return [];
    }

 

    // Generate date slots
    for (let i = startOffset; i < maxDays; i++) {
      const date = moment().add(i, 'days');
      
      // Skip if it's a holiday
      if (isHoliday(date)) {
   
        continue;
      }
      
      // Check if the day is closed
      const dayInfo = hotel.workingInfo?.find(w => w.day === date.day());
      if (!dayInfo || dayInfo.is_closed) {
     
        continue;
      }

      // Check if the day has running hours for any order type (DELIVERY or PICKUP)
      const hasOrderTypeHours = dayInfo.hotelDayInfo?.some(h => 
        (h.type === 'DELIVERY' || h.type === 'PICKUP') && 
        h.running_hours?.length > 0
      );

      // Skip if no order type has running hours
      if (!hasOrderTypeHours) {
        continue;
      }

      let label;
      if (i === 0) label = "Today";
      else if (i === 1) label = "Tomorrow";
      else label = date.format("dddd"); // Day name like "Friday"

      const dateSlot = {
        date: date.format("YYYY-MM-DD"),
        label,
        dayIndex: date.day(),
        moment: date.clone()
      };
      
      dates.push(dateSlot);
    }

    return dates;
  }, [hotel, isHotelAvailable, isHoliday]);

  // Check if scheduled is available (has any dates and slots)
  const isScheduledAvailable = useMemo(() => {

    if (!isHotelAvailable || getAvailableDates.length === 0) {
      return false;
    }
    
    // Check if any date has valid time slots for any order type
    // Note: We can't check for specific order type here since this hook doesn't know the current order type
    // The specific order type check should be done in the component using getTimeSlots
    return true;
  }, [isHotelAvailable, getAvailableDates]);

  // Check if today is a holiday (affects ASAP availability)
  const isTodayHoliday = useMemo(() => {
    return isHoliday(moment());
  }, [isHoliday]);

  // Check if a specific order type is available for a specific date
  const isOrderTypeAvailableForDate = useCallback((date, orderType) => {
    if (!hotel || !date || !orderType || !isHotelAvailable) {
      return false;
    }

    const momentDate = moment(date);
    const dayInfo = hotel.workingInfo?.find(w => w.day === momentDate.day());
    
    if (!dayInfo || dayInfo.is_closed) {
      return false;
    }

    // Check if it's a holiday
    if (isHoliday(momentDate)) {
      return false;
    }

    // Check if the specific order type has running hours
    const orderTypeInfo = dayInfo.hotelDayInfo?.find(h => h.type === orderType);
    if (!orderTypeInfo || !orderTypeInfo.running_hours?.length) {
      return false;
    }

    // Check if there are valid time slots for this order type and date
    const timeSlots = getTimeSlots(date, orderType);
    return timeSlots && timeSlots.length > 0;
  }, [hotel, isHotelAvailable, isHoliday, getTimeSlots]);

  return {
    getAvailableDates,
    getTimeSlots,
    isScheduledAvailable,
    isHotelAvailable,
    isTodayHoliday,
    isHoliday,
    getBufferTime,
    isOrderTypeAvailableForDate
  };
};

export default useScheduledSlots; 